/* eslint-disable @typescript-eslint/no-explicit-any */
import { Draggable, formatUnit, createElement, isNullOrUndefined as isNoU, addClass, closest } from '@syncfusion/ej2-base';
import { removeClass, remove } from '@syncfusion/ej2-base';
import * as cls from '../base/css-constant';
import * as events from '../base/constant';
/**
 * ColumnDragAndDrop module is used to perform column reordering actions.
 */
export class ColumnDragAndDrop {
    /**
     * Constructor for column drag and drop module
     *
     * @param {Kanban} parent Accepts the kanban instance
     * @private
     */
    constructor(parent) {
        this.parent = parent;
        this.isDragging = false;
    }
    wireColumnDragEvents(headerCell) {
        this.dragArea = this.parent.element;
        this.dragInstance = new Draggable(headerCell, {
            clone: true,
            dragArea: this.dragArea,
            dragStart: this.columnDragStart.bind(this),
            drag: this.columnDrag.bind(this),
            dragStop: this.columnDragStop.bind(this),
            helper: this.columnDragHelper.bind(this),
            enableTapHold: this.parent.isAdaptive
        });
        if (!this.dropIndicator) {
            this.dropIndicator = createElement('div', {
                className: 'e-kanban-column-indicator'
            });
            document.body.appendChild(this.dropIndicator);
        }
    }
    columnDragHelper(e) {
        this.draggedColumn = closest(e.sender.target, '.' + cls.HEADER_CELLS_CLASS);
        if (isNoU(this.draggedColumn)) {
            return null;
        }
        this.isStackedHeader = this.draggedColumn.classList.contains(cls.STACKED_HEADER_CELL_CLASS);
        if (this.isStackedHeader) {
            const stackedHeaderIndex = this.getColumnIndex(this.draggedColumn);
            const stackedHeader = this.parent.stackedHeaders[stackedHeaderIndex];
            const keyFields = stackedHeader.keyFields.split(',').map((field) => field.trim());
            this.contentCells = [];
            keyFields.forEach((key) => {
                const cellsForKey = Array.from(this.parent.element.querySelectorAll('.e-content-cells[data-key="' + key + '"]'));
                this.contentCells.push(...cellsForKey);
            });
        }
        else {
            const columnKey = this.draggedColumn.getAttribute('data-key');
            this.contentCells = Array.from(this.parent.element.querySelectorAll('.e-content-cells[data-key="' + columnKey + '"]'));
        }
        addClass([this.draggedColumn], 'e-kanban-dragging-header');
        this.draggedIndex = this.getColumnIndex(this.draggedColumn);
        if (!this.skeletonElement) {
            this.skeletonElement = createElement('div', {
                className: 'e-kanban-column-skeleton'
            });
            document.body.appendChild(this.skeletonElement);
        }
        this.createColumnSkeleton();
        return this.skeletonElement;
    }
    columnDragStart(e) {
        if (!this.parent.allowColumnDragAndDrop) {
            return;
        }
        this.isDragging = true;
        const dragStartArgs = {
            cancel: false,
            event: e,
            element: this.draggedColumn,
            fromIndex: this.draggedIndex,
            column: this.isStackedHeader ? this.parent.stackedHeaders[this.draggedIndex] : this.parent.columns[this.draggedIndex]
        };
        this.parent.trigger(events.columnDragStart, dragStartArgs);
        if (dragStartArgs.cancel) {
            this.resetDragState();
            this.dragInstance.intDestroy(e);
            return;
        }
        addClass([this.draggedColumn], 'e-dragged-header');
        this.contentCells.forEach((cell) => {
            addClass([cell], 'e-dragged-content');
        });
        this.showColumnSkeleton(e);
    }
    columnDrag(e) {
        if (!this.isDragging) {
            return;
        }
        const targetHeader = this.findTargetHeader(e);
        if (!targetHeader) {
            this.dropIndicator.style.display = 'none';
            return;
        }
        const draggedColumnKey = this.draggedColumn.getAttribute('data-key');
        const targetColumnKey = targetHeader.getAttribute('data-key');
        const draggedParentHeader = this.getParentStackedHeader(draggedColumnKey);
        const targetParentHeader = this.getParentStackedHeader(targetColumnKey);
        if (!this.isStackedHeader && draggedParentHeader) {
            if (!targetParentHeader) {
                this.dropIndicator.style.display = 'none';
                return;
            }
            else if (draggedParentHeader.keyFields !== targetParentHeader.keyFields) {
                this.dropIndicator.style.display = 'none';
                return;
            }
        }
        if (!this.isStackedHeader && draggedParentHeader && targetParentHeader) {
            if (draggedParentHeader.keyFields !== targetParentHeader.keyFields) {
                this.dropIndicator.style.display = 'none';
                return;
            }
        }
        this.targetIndex = this.getColumnIndex(targetHeader);
        const rect = targetHeader.getBoundingClientRect();
        const dragArgs = {
            event: e,
            element: this.draggedColumn,
            fromIndex: this.draggedIndex,
            toIndex: this.targetIndex,
            column: this.isStackedHeader ? this.parent.stackedHeaders[this.draggedIndex] : this.parent.columns[this.draggedIndex]
        };
        this.parent.trigger(events.columnDrag, dragArgs);
        if (dragArgs.cancel) {
            this.resetDragState();
            return;
        }
        this.dropIndicator.style.display = 'block';
        this.dropIndicator.style.left = (rect.left + window.pageXOffset) + 'px';
        this.dropIndicator.style.top = (rect.top + window.pageYOffset) + 'px';
        this.dropIndicator.style.height = rect.height + 'px';
    }
    columnDragStop(e) {
        if (!this.isDragging) {
            return;
        }
        const targetHeader = this.findTargetHeader(e);
        if (!targetHeader) {
            this.resetDragState();
            return;
        }
        const draggedColumnKey = this.draggedColumn.getAttribute('data-key');
        const targetColumnKey = targetHeader.getAttribute('data-key');
        const draggedParentHeader = this.getParentStackedHeader(draggedColumnKey);
        const targetParentHeader = this.getParentStackedHeader(targetColumnKey);
        if (!this.isStackedHeader && draggedParentHeader) {
            if (!targetParentHeader) {
                this.resetDragState();
                return;
            }
            else if (draggedParentHeader.keyFields !== targetParentHeader.keyFields) {
                this.resetDragState();
                return;
            }
        }
        if (!this.isStackedHeader && draggedParentHeader && targetParentHeader) {
            if (draggedParentHeader.keyFields !== targetParentHeader.keyFields) {
                this.resetDragState();
                return;
            }
        }
        const targetIndex = this.getColumnIndex(targetHeader);
        const dropIndex = targetIndex;
        const dropArgs = {
            cancel: false,
            event: e,
            element: this.draggedColumn,
            fromIndex: this.draggedIndex,
            toIndex: targetIndex,
            dropIndex: dropIndex,
            column: this.isStackedHeader ? this.parent.stackedHeaders[this.draggedIndex] : this.parent.columns[this.draggedIndex]
        };
        this.parent.trigger(events.columnDrop, dropArgs);
        if (!dropArgs.cancel && dropIndex !== this.draggedIndex) {
            if (this.isStackedHeader) {
                this.reorderStackedHeaders(this.draggedIndex, targetIndex);
            }
            else {
                this.reorderColumns(this.draggedIndex, targetIndex);
            }
        }
        this.resetDragState();
    }
    createColumnSkeleton() {
        if (!this.skeletonElement || !this.draggedColumn) {
            return;
        }
        this.skeletonElement.innerHTML = '';
        this.skeletonElement.style.display = 'block';
        this.skeletonElement.style.position = 'absolute';
        this.skeletonElement.style.zIndex = '999';
        this.skeletonElement.style.width = formatUnit(this.draggedColumn.offsetWidth);
        // Clone header
        const headerClone = this.draggedColumn.cloneNode(true);
        headerClone.classList.add('e-kanban-skeleton-header');
        this.skeletonElement.appendChild(headerClone);
        // Get the column key
        const columnKey = this.draggedColumn.getAttribute('data-key');
        const calculatedHeigt = this.parent.element.getBoundingClientRect().height;
        // Create content wrapper
        const contentWrapper = createElement('div', {
            className: 'e-kanban-skeleton-content'
        });
        const availableSpace = calculatedHeigt - 16; // 16px for padding
        if (this.isStackedHeader) {
            const cardHeight = this.contentCells[0].childNodes[1].firstChild
                ? this.contentCells[0].childNodes[1].firstChild.offsetHeight : 0;
            const numCards = cardHeight === 0 ? 0 : Math.floor(calculatedHeigt / (cardHeight));
            for (let i = 0; i < numCards; i++) {
                const placeholderCard = createElement('div', {
                    className: 'e-kanban-skeleton-card',
                    styles: 'height: ' + cardHeight + 'px;'
                });
                contentWrapper.appendChild(placeholderCard);
            }
        }
        else {
            const columnModel = this.parent.columns.find((col) => col.keyField.toString() === columnKey);
            const isCollapsed = columnModel && columnModel.allowToggle && !columnModel.isExpanded;
            if (isCollapsed) {
                // For collapsed columns, create a single placeholder with full content height
                const emptyContainer = createElement('div', {
                    className: 'e-kanban-skeleton-empty-content',
                    styles: 'height: ' + availableSpace + 'px;'
                });
                contentWrapper.appendChild(emptyContainer);
            }
            else {
                // Get the first real card height
                const firstCard = this.parent.element.querySelector('.e-content-cells[data-key="' + columnKey + '"] .e-card');
                let cardHeight;
                if (firstCard && firstCard.offsetHeight) {
                    cardHeight = Math.min(150, firstCard.offsetHeight); // Max 150px
                }
                const numCards = firstCard === null ? 0 : Math.floor(calculatedHeigt / (cardHeight));
                for (let i = 0; i < numCards; i++) {
                    const placeholderCard = createElement('div', {
                        className: 'e-kanban-skeleton-card',
                        styles: 'height: ' + cardHeight + 'px;'
                    });
                    contentWrapper.appendChild(placeholderCard);
                }
            }
        }
        // Explicitly set the height of the content wrapper to match contentTd height
        contentWrapper.style.height = calculatedHeigt + 'px';
        // Add content wrapper to skeleton
        this.skeletonElement.appendChild(contentWrapper);
        // Set skeleton height to match header + content
        this.skeletonElement.style.height = calculatedHeigt + 'px';
    }
    showColumnSkeleton(e) {
        if (!this.skeletonElement) {
            return;
        }
        if (this.draggedColumn) {
            const headerElement = closest(this.draggedColumn, '.e-kanban-header');
            if (headerElement && this.skeletonElement.parentElement !== headerElement) {
                headerElement.appendChild(this.skeletonElement);
            }
        }
        this.skeletonElement.style.display = 'block';
    }
    findTargetHeader(e) {
        const target = e.target;
        if (this.isStackedHeader) {
            const stackedHeader = closest(target, '.' + cls.STACKED_HEADER_CELL_CLASS);
            if (stackedHeader && stackedHeader !== this.draggedColumn) {
                return stackedHeader;
            }
        }
        else {
            const headerCell = closest(target, '.' + cls.HEADER_CELLS_CLASS);
            if (headerCell && headerCell !== this.draggedColumn) {
                return headerCell;
            }
        }
        return null;
    }
    getParentStackedHeader(columnKey) {
        if (!this.parent.stackedHeaders || this.parent.stackedHeaders.length === 0) {
            return null;
        }
        return this.parent.stackedHeaders.find((header) => {
            const keyFields = header.keyFields.split(',').map((key) => key.trim());
            return keyFields.indexOf(columnKey) !== -1;
        });
    }
    getColumnIndex(headerCell) {
        return Array.from(headerCell.parentElement.children).indexOf(headerCell);
    }
    isColumnVisible(column) {
        return this.parent.enableVirtualization
            ? this.parent.virtualLayoutModule.isColumnVisible(column)
            : this.parent.layoutModule.isColumnVisible(column);
    }
    getVisibleColumns(columns) {
        return columns
            .map((column, index) => ({ column, index }))
            .filter(({ column }) => this.isColumnVisible(column));
    }
    reorderColumns(fromIndex, toIndex) {
        const visibleColumns = this.getVisibleColumns(this.parent.columns);
        const sourceIndex = visibleColumns[fromIndex].index;
        const targetIndex = visibleColumns[toIndex].index;
        const columns = this.parent.columns.slice();
        const column = columns.splice(sourceIndex, 1)[0];
        columns.splice(targetIndex, 0, column);
        this.parent.columns = columns;
        if (this.parent.enableVirtualization) {
            this.parent.virtualLayoutModule.refresh();
        }
        else {
            this.parent.layoutModule.refresh();
        }
    }
    reorderStackedHeaders(sourceIndex, targetIndex) {
        const stackedHeadersList = this.parent.stackedHeaders.slice();
        const movingStackedHeader = stackedHeadersList.splice(sourceIndex, 1)[0];
        stackedHeadersList.splice(targetIndex, 0, movingStackedHeader);
        this.parent.stackedHeaders = stackedHeadersList;
        const headerToColumnsMap = new Map();
        stackedHeadersList.forEach((header) => {
            const keyFields = header.keyFields.split(',').map((key) => key.trim());
            headerToColumnsMap.set(header.keyFields, []);
            keyFields.forEach((keyField) => {
                const columnIndex = this.parent.columns.findIndex((column) => column.keyField.toString() === keyField);
                if (columnIndex !== -1) {
                    headerToColumnsMap.get(header.keyFields).push({
                        column: this.parent.columns[columnIndex],
                        index: columnIndex
                    });
                }
            });
        });
        const newColumnsArray = [];
        stackedHeadersList.forEach((header) => {
            const columnsForHeader = headerToColumnsMap.get(header.keyFields) || [];
            columnsForHeader.sort((a, b) => {
                return a.index - b.index;
            });
            columnsForHeader.forEach((item) => {
                newColumnsArray.push(item.column);
            });
        });
        this.parent.columns.forEach((column) => {
            const isInStackedHeader = stackedHeadersList.some((header) => {
                const keyFields = header.keyFields.split(',').map((key) => key.trim());
                return keyFields.indexOf(column.keyField.toString()) !== -1;
            });
            if (!isInStackedHeader && newColumnsArray.indexOf(column) === -1) {
                newColumnsArray.push(column);
            }
        });
        this.parent.columns = newColumnsArray;
        if (this.parent.enableVirtualization) {
            this.parent.virtualLayoutModule.refresh();
        }
        else {
            this.parent.layoutModule.refresh();
        }
    }
    resetDragState() {
        this.isDragging = false;
        if (this.dropIndicator) {
            this.dropIndicator.style.display = 'none';
        }
        if (this.skeletonElement) {
            this.skeletonElement.style.display = 'none';
        }
        if (this.draggedColumn) {
            removeClass([this.draggedColumn], ['e-kanban-dragging-header', 'e-dragged-header']);
        }
        if (this.contentCells && this.contentCells.length) {
            removeClass(this.contentCells, 'e-dragged-content');
        }
        this.draggedColumn = null;
        this.contentCells = null;
        this.isStackedHeader = false;
    }
    unwireColumnDragEvents(headerCell) {
        if (!isNoU(headerCell)) {
            const ej2Instances = headerCell.ej2_instances;
            if (!isNoU(ej2Instances) && ej2Instances.length > 0) {
                const dragInstance = ej2Instances[0];
                if (dragInstance && !dragInstance.isDestroyed) {
                    dragInstance.destroy();
                }
            }
        }
        // Remove drop indicator if this is the last header being unwired
        if (this.dropIndicator && this.dropIndicator.parentElement) {
            remove(this.dropIndicator);
            this.dropIndicator = null;
        }
        this.resetDragState();
    }
}
